import type { ChipTone } from '@/design';
import type {
  FirmStatus,
  IFigureSeries,
  IThreshold,
  IThresholdCondition
} from './api/metrics-updates-contracts';
import { formatMoney } from './components/sec-adv-values';
import { SCOPE_WORDS, wordFor } from './figures';

/**
 * The Metrics updates page's words (SR-315): a firm's status in the round, an edition's threshold
 * said as a sentence, the list fields the page asks for beside the threshold's figures, and the
 * keys the page, a row's work and the threshold tab spell a series and a row by. Words, not a
 * mirror: who is on either list is survey-api's.
 */

export const DATA_AS_OF = 'metrics.data_as_of';
export const RECEIVED = 'metrics.received';
export const REQUESTS = 'metrics.requests';
export const LAST_REQUEST = 'metrics.last_request_at';
export const FIRM = 'metrics.firm';
export const FIRM_STATUS = 'metrics.firm_status';

/** A firm status as its chip says it; one this table lacks drawn as spelled. */
export const FIRM_STATUS_WORDS: Readonly<Record<FirmStatus, { label: string; tone: ChipTone }>> = {
  supplies: { label: 'Firm supplies figures', tone: 'neutral' },
  direct: { label: 'Asked directly', tone: 'info' },
  not_set: { label: 'Firm status not set', tone: 'attention' },
  no_firm: { label: 'No firm', tone: 'attention' }
};

export function firmStatusChip(code: string): { label: string; tone: ChipTone } {
  return FIRM_STATUS_WORDS[code as FirmStatus] ?? { label: code, tone: 'neutral' };
}

/** A series as one key, `metric|scope` — a threshold column, a figure box. */
export function seriesKey(series: Pick<IThresholdCondition, 'metric' | 'scope'>): string {
  return `${series.metric}|${series.scope}`;
}

/** A threshold's conditions, its bands' included: a banded threshold may hold none of its own. */
export function conditionsOf(threshold: Nullable<IThreshold>): IThresholdCondition[] {
  return [...(threshold?.conditions ?? []), ...(threshold?.groups ?? []).flatMap((group) => group.conditions)];
}

/** A row's person as the rows read keys its editions and where else it owes figures,
 *  `<participantId>~<survey>`. */
export function pairKey(row: { participantId: string; survey: string }): string {
  return `${row.participantId}~${row.survey}`;
}

/** A series by the catalogue's label, else its metric's code, and its scope — "Custodied assets
 *  (Individual)": the catalogue labels the metric, and two scopes share it. */
export function seriesLabel(series: readonly IFigureSeries[], metric: string, scope: string): string {
  const named = series.find((entry) => entry.metric === metric && entry.scope === scope);
  return `${named?.label ?? metric} (${wordFor(SCOPE_WORDS, scope)})`;
}

const COMPACT = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });

/** An amount as a threshold is spoken of — "$120M", "$1.5M", "$900K"; the editor shows it whole. */
export function compactMoney(amount: number): string {
  for (const [size, unit] of [[1e9, 'B'], [1e6, 'M'], [1e3, 'K']] as const) {
    if (Math.abs(amount) >= size) return `$${COMPACT.format(amount / size)}${unit}`;
  }
  return formatMoney(amount);
}

function conditionWords(series: readonly IFigureSeries[], condition: IThresholdCondition): string {
  const label = seriesLabel(series, condition.metric, condition.scope);
  return condition.min === null ? label : `${label} ≥ ${compactMoney(condition.min)}`;
}

/** A band's territories — named while few, counted past three. */
function territoriesWords(territories: readonly string[]): string {
  return territories.length <= 3 ? territories.join(', ') : `${territories.length} territories`;
}

function conditionsWords(
  conditions: readonly IThresholdCondition[],
  combine: IThreshold['combine'],
  series: readonly IFigureSeries[]
): string {
  return conditions.map((condition) => conditionWords(series, condition)).join(combine === 'all' ? ' and ' : ' or ');
}

/** "Custodied assets (Individual) ≥ $150M or Production (Individual) ≥ $1M — down to 10 % below";
 *  a banded threshold says each band by its territories, then the rest: "31 territories: … ≥
 *  $120M; CA, NY: … ≥ $270M; elsewhere: not listed". */
export function thresholdWords(threshold: IThreshold, series: readonly IFigureSeries[]): string {
  const margin = threshold.marginPercent ? ` — down to ${threshold.marginPercent} % below` : '';
  const groups = threshold.groups ?? [];
  if (groups.length === 0) return `${conditionsWords(threshold.conditions, threshold.combine, series)}${margin}`;
  const bands = groups.map(
    (group) => `${territoriesWords(group.territories)}: ${conditionsWords(group.conditions, threshold.combine, series)}`
  );
  const rest =
    threshold.conditions.length > 0
      ? `elsewhere: ${conditionsWords(threshold.conditions, threshold.combine, series)}`
      : 'elsewhere: not listed';
  return `${[...bands, rest].join('; ')}${margin}`;
}

/** How a row's "also owes" entry relates to it, in words. */
export function relationWords(relation: string): string {
  switch (relation) {
    case 'same':
      return 'same person';
    case 'same_account':
      return 'same login';
    case 'led_team':
      return 'the team they lead';
    case 'team_lead':
      return 'the team’s lead';
    default:
      return relation;
  }
}
